/* eslint-disable @typescript-eslint/no-explicit-any */

import { publishErrorUnlessSuccess, hFetch, type HError } from './hFetch';
import type { UserEntity, UserCalendarAvailabilitySlotEntity } from './entity_types/User.ts';
import type { FeatureFlags } from './feature_flags.ts';

export type UserRole = 'Admin' | 'Agent' | 'Contributor';

export type GetUserResponse = {
  record: UserEntity;
  pending: string | null;
  ica_agreement_session_id: string | null;
  user_calendar: any;
  user_calendar_availability: UserCalendarAvailabilitySlotEntity[];
  team_slug: string;
};

export type GetUserDetailsResponse = {
  username: string;
  unread_count: number;
  incomplete_tasks: number;
  locked: boolean;
  role: UserRole;
  logo: string;
  impersonated: boolean;
  feature_flags?: FeatureFlags;
  onboarding_path: string;
  onboarding_completed: boolean;
  use_onboarding_invite_flow: boolean;
  team_slug?: string;
  team_phone_number?: string;
};

export type User = {
  email?: string;
  first_name?: string;
  last_name?: string;
  mobile_phone_number?: string;
  notify_on_new_message?: boolean;
  notify_on_new_application?: boolean;
  notify_daily_pipeline_report?: boolean;
  notify_weekly_digest_report?: boolean;
  notify_on_event_scheduled?: boolean;
  notify_on_event_reminder_email?: boolean;
  notify_on_event_reminder_sms?: boolean;
  notify_on_stale_jobs?: boolean;
  onboarding_completed?: boolean;
  password?: string;
  password_confirmation?: string;
  resend_invite?: boolean;
  role?: UserRole;
  status?: 'Active' | 'Inactive' | 'Pending';
  email_signature?: string;
};

export type CreateUser = {
  email: string;
  role: string;
};

// 1 is hardcoded for now until the API is updated to utilize the current user as a resource
// will not affect desired result
export const getUser = async ({ id } = { id: 1 }): Promise<GetUserResponse> => {
  const data = await hFetch<GetUserResponse>({ url: `/api/users/${id}` });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Get User' });
  return data as GetUserResponse;
};

export const getUserDetails = async () => {
  const data = await hFetch({ url: `/api/users/details` });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Get User Details' });
  return data as GetUserDetailsResponse;
};

export type VerifyUserEmailReturn = { status: 'success' | 'error'; message: string; title?: string };
export const verifyUserEmail = async ({ id } = { id: 1 }): Promise<VerifyUserEmailReturn> => {
  return hFetch({ url: `/api/users/${id}/verify_email`, method: 'POST' }) as unknown as VerifyUserEmailReturn;
};

export const resendEmailConfirmation = async ({ id } = { id: 1 }) => {
  const data = await hFetch({ url: `/api/users/${id}/resend_email_confirmation`, method: 'POST' });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Resend Email Confirmation' });
  return data;
};

export const updateUser = async (
  { id, publishError = true }: { id: string; publishError?: boolean },
  { updatedUserAttributes }: { updatedUserAttributes: User }
) => {
  const data = await hFetch({ url: `/api/users/${id}`, method: 'PATCH', payload: updatedUserAttributes });
  if (publishError) publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Update User' });
  return data as { status: 'success' | 'error'; message?: string };
};

export const createUser = async (newUserAttributes: CreateUser) => {
  return await hFetch({ url: `/api/users`, method: 'POST', payload: newUserAttributes });
};
